import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiMinus, FiTarget, FiAlertTriangle } from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import Button from '../../../components/common/Button';
import {
  Section,
  SectionTitle,
  Grid,
  Card,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  ProgressBar,
  ProgressFill,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const PerformanceAnalytics = ({ studentId, days = 90 }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId, days]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await parentAnalyticsAPI.getPerformanceAnalytics(studentId, { days });
      setData(response.data.performance_analytics);
    } catch (error) {
      showToast('error', 'Failed to load performance analytics');
      console.error('Error fetching performance analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <FiTrendingUp style={{ color: theme.colors.success }} />;
    if (trend === 'declining') return <FiTrendingDown style={{ color: theme.colors.error }} />;
    return <FiMinus style={{ color: theme.colors.textSecondary }} />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'improving') return theme.colors.success;
    if (trend === 'declining') return theme.colors.error;
    return theme.colors.textSecondary;
  };

  const getMasteryColor = (level) => {
    const colors = {
      expert: '#10B981',
      proficient: '#3B82F6',
      developing: '#F59E0B',
      beginner: '#EF4444',
    };
    return colors[level?.toLowerCase()] || theme.colors.textSecondary;
  };

  const prepareTimelineData = () => {
    if (!data || !data.performance_timeline) return [];
    
    return data.performance_timeline.map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      score: item.score,
      passed: item.passed,
      timestamp: new Date(item.date).getTime(),
    }));
  };

  const prepareMovingAverageData = () => {
    if (!data || !data.moving_averages) return [];
    
    return data.moving_averages.map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      average: item.moving_average,
    }));
  };

  const prepareCombinedTimelineData = () => {
    const timeline = prepareTimelineData();
    const movingAvg = prepareMovingAverageData();
    
    // Merge both datasets
    const combined = timeline.map((item, idx) => ({
      ...item,
      movingAverage: movingAvg[idx]?.average,
    }));
    
    return combined;
  };

  const prepareSubjectMasteryData = () => {
    if (!data || !data.subject_mastery) return [];
    
    return data.subject_mastery
      .sort((a, b) => b.average_score - a.average_score)
      .map((subject) => ({
        subject: subject.subject_name,
        score: subject.average_score,
        passRate: subject.pass_rate,
        attempts: subject.attempts,
        mastery: subject.mastery_level,
      }));
  };

  const prepareSubjectRadarData = () => {
    if (!data || !data.subject_mastery) return [];
    
    return data.subject_mastery.slice(0, 6).map((subject) => ({
      subject: subject.subject_name.length > 15 
        ? subject.subject_name.substring(0, 12) + '...' 
        : subject.subject_name,
      score: subject.average_score,
      fullMark: 100,
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: theme.shadows.lg,
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.colors.textPrimary }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0 0 0', color: entry.color, fontSize: '14px' }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
              {entry.name.includes('Score') || entry.name.includes('Average') ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading performance data..." />
      </LoadingContainer>
    );
  }

  if (!data || !data.overall) {
    return (
      <EmptyState>
        <FiTarget size={64} />
        <h3>No Performance Data</h3>
        <p>Performance analytics will appear once quizzes are completed</p>
      </EmptyState>
    );
  }

  return (
    <>
      {/* Overall Performance Stats */}
      <Section>
        <SectionTitle>
          <FiTarget />
          Overall Performance
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Average Score</StatLabel>
            <StatValue color={theme.colors.primary}>
              {data.overall.average_score.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Rating: {'⭐'.repeat(Math.min(data.overall.rating || 0, 5))}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Pass Rate</StatLabel>
            <StatValue color={theme.colors.success}>
              {data.overall.pass_rate.toFixed(1)}%
            </StatValue>
            <StatDescription>
              {data.overall.passed_quizzes} / {data.overall.total_quizzes} quizzes passed
            </StatDescription>
            <div style={{ marginTop: '12px' }}>
              <ProgressBar>
                <ProgressFill
                  percentage={data.overall.pass_rate}
                  color={theme.colors.success}
                />
              </ProgressBar>
            </div>
          </StatCard>

          <StatCard>
            <StatLabel>Recent Average</StatLabel>
            <StatValue color={theme.colors.info}>
              {data.overall.recent_average.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Last 5 quizzes
            </StatDescription>
          </StatCard>

          <StatCard>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <StatLabel>Trajectory</StatLabel>
              {getTrendIcon(data.trajectory.trend)}
            </div>
            <StatValue color={getTrendColor(data.trajectory.trend)} style={{ textTransform: 'capitalize' }}>
              {data.trajectory.trend}
            </StatValue>
            <StatDescription>
              {data.trajectory.description}
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Performance Timeline */}
      {data.performance_timeline && data.performance_timeline.length > 0 && (
        <Section>
          <SectionTitle>
            Performance Timeline
          </SectionTitle>
          <Card>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '20px' }}>
              Quiz scores over time with 7-quiz moving average
            </p>
            <ChartContainer height="450px" mobileHeight="350px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={prepareCombinedTimelineData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    tick={{ fill: theme.colors.textSecondary }}
                    domain={[0, 100]}
                    label={{
                      value: 'Score (%)',
                      angle: -90,
                      position: 'insideLeft',
                      fill: theme.colors.textSecondary,
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={theme.colors.primary}
                    strokeWidth={2}
                    dot={{ r: 6, fill: theme.colors.primary }}
                    name="Quiz Score"
                  />
                  {data.moving_averages && data.moving_averages.length > 0 && (
                    <Line
                      type="monotone"
                      dataKey="movingAverage"
                      stroke={theme.colors.secondary}
                      strokeWidth={3}
                      dot={false}
                      name="Moving Average"
                      strokeDasharray="5 5"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: theme.colors.backgroundSecondary,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {getTrendIcon(data.trajectory.trend)}
              <p style={{ margin: 0, fontSize: '14px', color: theme.colors.textPrimary }}>
                <strong>Trend:</strong> {data.trajectory.description}
              </p>
            </div>
          </Card>
        </Section>
      )}

      {/* Subject Mastery */}
      {data.subject_mastery && data.subject_mastery.length > 0 && (
        <>
          <Section>
            <SectionTitle>
              Subject Mastery Levels
            </SectionTitle>
            <Card>
              <ChartContainer height="400px" mobileHeight="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={prepareSubjectMasteryData()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: theme.colors.textSecondary }} />
                    <YAxis
                      type="category"
                      dataKey="subject"
                      tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                      width={120}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" fill={theme.colors.primary} name="Average Score" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </Card>
          </Section>

          {/* Subject Mastery Details */}
          <Section>
            <SectionTitle>
              Detailed Subject Performance
            </SectionTitle>
            <Grid minWidth="300px">
              {data.subject_mastery.map((subject) => (
                <StatCard key={subject.subject_id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <StatLabel>{subject.subject_name}</StatLabel>
                    <Badge color={getMasteryColor(subject.mastery_level)}>
                      {subject.mastery_level}
                    </Badge>
                  </div>
                  <StatValue color={theme.colors.primary}>
                    {subject.average_score.toFixed(1)}%
                  </StatValue>
                  <div style={{ marginTop: '12px' }}>
                    <ProgressBar>
                      <ProgressFill
                        percentage={subject.average_score}
                        color={getMasteryColor(subject.mastery_level)}
                      />
                    </ProgressBar>
                  </div>
                  <StatDescription style={{ marginTop: '12px' }}>
                    Pass Rate: {subject.pass_rate.toFixed(0)}% • {subject.attempts} attempts •{' '}
                    {'⭐'.repeat(Math.min(subject.rating || 0, 5))}
                  </StatDescription>
                </StatCard>
              ))}
            </Grid>
          </Section>

          {/* Subject Radar Chart */}
          {data.subject_mastery.length >= 3 && (
            <Section>
              <SectionTitle>
                Subject Performance Overview
              </SectionTitle>
              <Card>
                <ChartContainer height="450px" mobileHeight="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={prepareSubjectRadarData()}>
                      <PolarGrid stroke={theme.colors.border} />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        tick={{ fill: theme.colors.textSecondary }}
                      />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke={theme.colors.primary}
                        fill={theme.colors.primary}
                        fillOpacity={0.6}
                      />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </Card>
            </Section>
          )}
        </>
      )}

      {/* Weak Areas */}
      {data.weak_areas && data.weak_areas.length > 0 && (
        <Section>
          <SectionTitle>
            <FiAlertTriangle />
            Areas Needing Attention
          </SectionTitle>
          <Grid minWidth="300px">
            {data.weak_areas.map((area) => (
              <Card
                key={area.quiz_id}
                style={{
                  borderLeft: `4px solid ${theme.colors.warning}`,
                  background: `${theme.colors.warning}10`,
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <Badge color={theme.colors.warning}>
                    {area.fail_count} failed attempts
                  </Badge>
                </div>
                <h3 style={{
                  margin: '0 0 8px 0',
                  color: theme.colors.textPrimary,
                  fontSize: '18px',
                }}>
                  {area.quiz_title}
                </h3>
                <p style={{ margin: '0 0 8px 0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                  Subject: {area.subject_name}
                </p>
                <p style={{ margin: '0 0 12px 0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                  Course: {area.course_title}
                </p>
                <Button
                  size="small"
                  variant="outline"
                  style={{ width: '100%' }}
                >
                  Review Material
                </Button>
              </Card>
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
};

export default PerformanceAnalytics;

